import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PROJECTS, PROJECT_CATEGORIES, PROJECT_STATUS_COLORS } from "../config/wiseboardConfig";
import type { ProjectStatus } from "../config/wiseboardConfig";
import { toast } from "sonner";
import { CheckCircle, Clock, AlertTriangle, Send, FileText } from "lucide-react";
import BoardDecisionForm from "../components/BoardDecisionForm";
import SynthesisWorkspace from "../components/SynthesisWorkspace";
import SalesActivationWorkspace from "../components/SalesActivationWorkspace";

export default function ProjectWorkspace() {
  const { caseId, projectId: rawProjectId } = useParams<{ caseId: string; projectId: string }>();
  const projectType = (rawProjectId || "").toUpperCase().replace("-", "_");
  const project = PROJECTS[projectType];
  const isP21 = projectType === "P21";
  const isP20 = projectType === "P20";
  const isP22 = projectType === "P22";

  const [dbProject, setDbProject] = useState<any>(null);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [artifactContent, setArtifactContent] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId || !projectType) return;
    const load = async () => {
      const { data: proj } = await supabase
        .from("wiseboard_projects")
        .select("*")
        .eq("case_id", caseId)
        .eq("project_type", projectType)
        .single();
      if (proj) setDbProject(proj);

      if (proj) {
        const { data: arts } = await supabase
          .from("wiseboard_artifacts")
          .select("*")
          .eq("project_id", proj.id)
          .order("created_at", { ascending: false });
        if (arts && arts.length > 0) {
          setArtifacts(arts);
          setArtifactContent(arts[0].content || "");
        }
      }
      setLoading(false);
    };
    load();
  }, [caseId, projectType]);

  if (!project) return <div className="p-6">Project not found</div>;
  if (loading) return <div className="p-6 text-muted-foreground">Loading workspace...</div>;

  const catInfo = PROJECT_CATEGORIES[project.category];
  const status = (dbProject?.status || "not_started") as ProjectStatus;

  const updateStatus = async (newStatus: ProjectStatus) => {
    if (!dbProject) return;
    const updates: any = { status: newStatus };
    if (newStatus === "in_progress" && !dbProject.started_at) updates.started_at = new Date().toISOString();
    if (newStatus === "approved") updates.completed_at = new Date().toISOString();
    await supabase.from("wiseboard_projects").update(updates).eq("id", dbProject.id);
    setDbProject({ ...dbProject, ...updates });
    toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
  };

  const saveArtifact = async () => {
    if (!dbProject) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); setSaving(false); return; }

    const latestVersion = artifacts.length > 0 ? Math.max(...artifacts.map(a => a.version)) : 0;
    const { data, error } = await supabase.from("wiseboard_artifacts").insert({
      project_id: dbProject.id,
      user_id: user.id,
      name: `${project.id} — ${project.name}`,
      version: latestVersion + 1,
      content: artifactContent,
      approval_status: "draft",
    }).select().single();
    if (!error && data) {
      setArtifacts([data, ...artifacts]);
      toast.success(`Artifact v${data.version} saved`);
    }
    setSaving(false);
  };

  const sendToAI = async () => {
    if (!chatInput.trim()) return;
    setAiLoading(true);
    const newMessages = [...chatMessages, { role: "user", content: chatInput }];
    setChatMessages(newMessages);
    setChatInput("");

    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: newMessages,
          model: "google/gemini-2.5-pro",
          systemPrompt: project.systemPrompt,
        },
      });
      if (error) throw error;
      const reply = data?.choices?.[0]?.message?.content || "No response received.";
      setChatMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch (err: any) {
      toast.error("AI error: " + (err.message || "Unknown"));
    }
    setAiLoading(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-4 w-4 rounded-full ${catInfo.color}`} />
          <div>
            <h1 className="text-xl font-bold">{project.id.replace("_", " ")} — {project.name}</h1>
            <p className="text-sm text-muted-foreground">{project.legacyName} · {catInfo.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={PROJECT_STATUS_COLORS[status]}>{status.replace("_", " ")}</Badge>
          {status === "not_started" && <Button size="sm" onClick={() => updateStatus("in_progress")}>Start</Button>}
          {status === "in_progress" && <Button size="sm" variant="default" onClick={() => updateStatus("approved")}>Approve</Button>}
          {status === "approved" && <Button size="sm" variant="outline" onClick={() => updateStatus("in_progress")}>Reopen</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Role & Scope + Artifact Editor */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Role & Scope</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div><span className="font-medium">Role:</span> {project.role}</div>
              <div><span className="font-medium">Scope:</span> {project.scope}</div>
              <div>
                <span className="font-medium">Must Do:</span>
                <ul className="list-disc ml-5 mt-1 text-xs text-muted-foreground">
                  {project.mustDo.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
              <div>
                <span className="font-medium">Must Not:</span>
                <ul className="list-disc ml-5 mt-1 text-xs text-muted-foreground">
                  {project.mustNot.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            </CardContent>
          </Card>

          {isP22 && dbProject ? (
            <SalesActivationWorkspace
              caseId={caseId!}
              dbProject={dbProject}
              artifacts={artifacts}
              onArtifactSaved={async () => {
                const { data: arts } = await supabase
                  .from("wiseboard_artifacts")
                  .select("*")
                  .eq("project_id", dbProject.id)
                  .order("created_at", { ascending: false });
                if (arts) { setArtifacts(arts); }
              }}
            />
          ) : isP20 && dbProject ? (
            <SynthesisWorkspace
              caseId={caseId!}
              dbProject={dbProject}
              artifacts={artifacts}
              onArtifactSaved={async () => {
                const { data: arts } = await supabase
                  .from("wiseboard_artifacts")
                  .select("*")
                  .eq("project_id", dbProject.id)
                  .order("created_at", { ascending: false });
                if (arts) { setArtifacts(arts); }
              }}
            />
          ) : isP21 && dbProject ? (
            <BoardDecisionForm
              caseId={caseId!}
              dbProject={dbProject}
              artifacts={artifacts}
              onArtifactSaved={async () => {
                const { data: arts } = await supabase
                  .from("wiseboard_artifacts")
                  .select("*")
                  .eq("project_id", dbProject.id)
                  .order("created_at", { ascending: false });
                if (arts) { setArtifacts(arts); }
              }}
            />
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Artifact Output</CardTitle>
                  <Button size="sm" variant="outline" onClick={saveArtifact} disabled={saving}>
                    <FileText className="h-3.5 w-3.5 mr-1" />{saving ? "Saving..." : "Save"}
                  </Button>
                </div>
                <CardDescription className="text-xs">Expected: {project.outputArtifact}</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={artifactContent}
                  onChange={e => setArtifactContent(e.target.value)}
                  placeholder={`Write or paste the ${project.outputArtifact} here...`}
                  rows={12}
                  className="text-sm font-mono"
                />
                {artifacts.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {artifacts.map(a => (
                      <Badge key={a.id} variant="outline" className="text-[10px] cursor-pointer" onClick={() => setArtifactContent(a.content || "")}>
                        v{a.version} — {a.approval_status}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: AI Chat */}
        <Card className="flex flex-col max-h-[calc(100vh-12rem)]">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm">AI Assistant — {project.name}</CardTitle>
            <CardDescription className="text-xs">Uses project-specific system prompt and reasoning rules</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto space-y-3 mb-3 min-h-[200px]">
              {chatMessages.length === 0 && (
                <div className="text-xs text-muted-foreground italic p-4 text-center">
                  {project.helpPrompt}
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`text-sm rounded-lg p-3 ${msg.role === "user" ? "bg-primary/10 ml-8" : "bg-muted mr-8"}`}>
                  <div className="font-medium text-xs mb-1 text-muted-foreground">{msg.role === "user" ? "You" : "AI"}</div>
                  <div className="whitespace-pre-wrap text-xs">{msg.content}</div>
                </div>
              ))}
              {aiLoading && <div className="text-xs text-muted-foreground animate-pulse">AI is thinking...</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Ask the AI assistant..."
                rows={2}
                className="text-sm"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendToAI(); } }}
              />
              <Button size="icon" onClick={sendToAI} disabled={aiLoading || !chatInput.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
